import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Box, Button, EntryList, Flex, Icon, Name, Text } from '~/components/blocks';
import { ContactMethods } from '~/components/partials/PatientDetailDialog/PatientPane/ContactMethods';
import { useFetchLineProfile } from '~/components/partials/PatientDetailDialog/PatientPane/PatientProfile/use-fetch-line_profile';
import {
  DirectVisitorDetailDialogProfileFragment,
  LineVisitorDetailDialogProfileFragment,
  PatientDetailDialogProfileFragment,
  WebVisitorDetailDialogProfileFragment,
} from '~/graphql/generated/graphql';
import { patientDetailDialogProfileState } from '~/state/partials/patient_detail_dialog/atoms';
import { Label } from '~/utils/label';
import { getPfTel } from '~/utils/pf_contact_point';

import { ProfileSkeleton } from './ProfileSkeleton';

type Props = {
  icon?: boolean;
  loading?: boolean;
  patient:
    | DirectVisitorDetailDialogProfileFragment
    | WebVisitorDetailDialogProfileFragment
    | PatientDetailDialogProfileFragment
    | LineVisitorDetailDialogProfileFragment;
  visitedAt?: string;
};

const PatientAge = styled(Text)(() =>
  css({
    lineHeight: 1,
  }),
);

export const PatientProfile = React.memo((props: Props) => {
  const { loading, patient, visitedAt } = props;
  const theme = useTheme();
  const setPatientDetailDialogProfileState = useSetAtom(patientDetailDialogProfileState);
  const canEdit =
    patient.__typename === 'DirectVisitor' && !patient?.outpatientQuestionnaireEntry?.id;

  const tel =
    patient.__typename === 'DirectVisitor' ||
    patient.__typename === 'WebVisitor' ||
    patient.__typename === 'LineVisitor'
      ? patient.mobilePhone
      : patient.__typename === 'Patient' && getPfTel(patient.pfPatient?.telecoms || []);

  const hasApp = patient.__typename === 'Patient';
  const hasSms = patient.__typename === 'DirectVisitor' || patient.__typename === 'WebVisitor';

  const isDeactivatedPatient = patient.__typename === 'Patient' && !patient.active;
  const { active } = useFetchLineProfile({ patient });
  const hasLine = patient.__typename === 'LineVisitor';
  const isDeactivatedLineVisitor = patient.__typename === 'LineVisitor' && !active;

  const handleEdit = useCallback(
    () => setPatientDetailDialogProfileState({ type: 'edit' as const }),
    [setPatientDetailDialogProfileState],
  );
  return !loading ? (
    <Box>
      <Flex alignItems="end" marginBottom={theme.space.l}>
        <Name
          size="l"
          familyName={patient.familyName}
          givenName={patient.givenName}
          phoneticFamilyName={patient.phoneticFamilyName}
          phoneticGivenName={patient.phoneticGivenName}
        />
        <PatientAge marginLeft={theme.space.m}>
          {`${Label.age(patient.birthDate)}（${Label.sex(patient.sex)}）`}
        </PatientAge>
      </Flex>

      <EntryList size="s">
        <EntryList.Head>生年月日</EntryList.Head>
        <EntryList.Body>{Label.warekiBirthDate(patient.birthDate)}</EntryList.Body>
      </EntryList>
      <EntryList size="s">
        <EntryList.Head>電話番号（携帯）</EntryList.Head>
        <EntryList.Body> {tel || '未登録'}</EntryList.Body>
      </EntryList>
      {canEdit && (
        <Button
          wide="fill"
          marginBottom={theme.space.xl}
          marginTop={theme.space.l}
          onClick={handleEdit}
        >
          <Icon icon="edit" size="m" />
          <Text size="xs">編集</Text>
        </Button>
      )}
      <EntryList size="s">
        <EntryList.Head>最終来局日</EntryList.Head>
        <EntryList.Body>{visitedAt ? Label.YYYYMMDDja(visitedAt) : '-'}</EntryList.Body>
      </EntryList>
      <EntryList size="s">
        <EntryList.Head>連絡ツール</EntryList.Head>
        <EntryList.Body>
          <ContactMethods
            hasApp={hasApp}
            hasSms={hasSms}
            hasLine={hasLine}
            isDeactivatedPatient={isDeactivatedPatient}
            isDeactivatedLineVisitor={isDeactivatedLineVisitor}
          />
        </EntryList.Body>
      </EntryList>
    </Box>
  ) : (
    <ProfileSkeleton />
  );
});

PatientProfile.displayName = 'PatientProfile';
